import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  effects as profileEffects,
  selectors as profileSelectors,
} from 'lib/entities/profile';
import auth from 'utils/auth';
import Router from 'next/router';
import orderbookSlice from 'global/MainLayout/SideWidget/Watchlist/Orderbook/slice';
import usePaywall from 'features/paywall/store/useChartbit';
import ERROR_MESSAGE from 'constants/errorMessage';
import ChartbitLayout from './Layout';
import { isSideToolbarOpen, isWidgetbarMinimized } from './TradingView/utils';
import { useChartbitStore } from './chartbit.store';

const { actions: orderbookActions } = orderbookSlice;

const Chartbit = dynamic(() => import('.'), {
  ssr: false,
  loading: () => <div />,
});

const ChartbitMyInvestment = dynamic(() => import('./MyInvestment'), {
  ssr: false,
  loading: () => <div />,
});

const TradeButton = dynamic(() => import('./TradeButton'), {
  ssr: false,
  loading: () => <div />,
});

const Paywall = dynamic(() => import('features/paywall/Wrapper/Chartbit'), {
  ssr: false,
  loading: () => <div />,
});

const Menu = dynamic(() => import('./Menu'), {
  ssr: false,
  loading: () => <div />,
});

type ChartbitWebProps = {
  symbol: string;
};

function ChartbitWeb({ symbol }: ChartbitWebProps) {
  const dispatch = useDispatch();
  const [theme, setTheme] = useState<string>('');
  const { userSettings } = useChartbitStore.getState();
  const [isChartReady, setChartReady] = useState<boolean>(false);
  const [hideLogo, setHideLogo] = useState<boolean>(true);
  const hide = useRef<boolean>(hideLogo);
  const [isWBMinimized, setIsWBMinimized] = useState<boolean>(
    isWidgetbarMinimized(),
  );
  const isMounted = useRef<boolean>(false);
  const initialSetUserSetting = useRef<boolean>(false);

  const {
    data: { id: userId },
  } = useSelector(profileSelectors);
  const { isPro, isLoading, isRenewal } = usePaywall(symbol);

  useEffect(() => {
    if (!initialSetUserSetting.current) {
      hide.current = !isSideToolbarOpen();
      setHideLogo(hide.current);
      initialSetUserSetting.current = true;
    }
  }, [userSettings['ChartDrawingToolbarWidget.visible']]);

  useEffect(() => {
    // @ts-ignore
    const { username } = auth.getUserAccess();
    if (!userId) {
      // @ts-ignore
      dispatch(profileEffects.getUserProfile(username));
    }
    isMounted.current = true;
  }, [dispatch, userId]);

  const handleChartReady = useCallback(() => {
    hide.current = !isSideToolbarOpen();
    setHideLogo(hide.current);

    setChartReady(true);
  }, []);
  const handleChartUnmount = useCallback(() => setChartReady(false), []);
  const handleToggleSidebar = useCallback(
    (isHide: boolean) => {
      hide.current = isHide;
      setHideLogo(hide.current);
    },
    [setHideLogo],
  );
  const handleToggleWidgetbar = useCallback((minimized: boolean) => {
    setIsWBMinimized(minimized);
  }, []);
  const handleSymbolChange = useCallback((targetSymbol: string) => {
    // Sync App Router Symbol with TradingView Active Symbol
    // https://github.com/vercel/next.js/issues/18127
    Router.push(`/symbol/${targetSymbol}/chartbit`, undefined, {
      shallow: true,
    });

    // Syns Sidewidget Orderbook Symbol
    dispatch(orderbookActions.setSymbol({ symbol: targetSymbol }));
  }, []);

  const handleResolveSymbolError = useCallback(
    (err: any, errSymbol: string) => {
      // handle paywall error
      const errorType = err?.cause?.errorType;
      if (errorType === ERROR_MESSAGE.PAYWALL_ERROR) {
        const redirectUrl = `/symbol/${errSymbol}/chartbit`;
        Router.push(redirectUrl);
      }
    },
    [],
  );

  const handleChangeTheme = (value: string) => {
    setTheme(value);
  };

  useEffect(() => {
    if (hideLogo !== hide.current) setHideLogo(hide.current);
  }, [hideLogo]);

  if (!isPro && !userId) {
    return null;
  }

  if (!isPro && !isLoading && isMounted.current) {
    return <Paywall isRenewal={isRenewal} />;
  }

  return (
    <ChartbitLayout>
      {isChartReady && (
        <>
          {!hideLogo && <Menu symbol={symbol} theme={theme} />}
          <TradeButton symbol={symbol} />
          <ChartbitMyInvestment
            symbol={symbol}
            isWidgetBarMinimized={isWBMinimized}
          />
        </>
      )}
      <Chartbit
        symbol={symbol}
        onChartReady={handleChartReady}
        onChartUnmount={handleChartUnmount}
        onToggleSidebar={handleToggleSidebar}
        onToggleWidgetbar={handleToggleWidgetbar}
        onSymbolChange={handleSymbolChange}
        onResolveSymbolError={handleResolveSymbolError}
        onChangeThemeChartbit={handleChangeTheme}
      />
    </ChartbitLayout>
  );
}

export default ChartbitWeb;
